import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axiosInstance from '../api';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const TEMPLATE_META = {
  BOARD_SUMMARY:     { pages: 1,  icon: 'fa-chalkboard-teacher', color: '#7c3aed', descKey: 'descBoardSummary' },
  EXECUTIVE_BRIEF:   { pages: 4,  icon: 'fa-briefcase',          color: '#2563eb', descKey: 'descExecutiveBrief' },
  QUARTERLY_REVIEW:  { pages: 8,  icon: 'fa-chart-line',         color: '#16a34a', descKey: 'descQuarterlyReview' },
};

const STATUS_BADGE = {
  DRAFT:      { bg: '#f3f4f6', color: '#374151' },
  FINALISED:  { bg: '#dcfce7', color: '#166534' },
};

function RiskGauge({ score }) {
  const { t } = useTranslation();
  const pct   = Math.min(100, Math.max(0, score || 0));
  const color = pct >= 75 ? '#16a34a' : pct >= 50 ? '#d97706' : pct >= 25 ? '#ea580c' : '#dc2626';
  const label = pct >= 75 ? t('ExecutiveReports.riskLow') : pct >= 50 ? t('ExecutiveReports.riskModerate') : pct >= 25 ? t('ExecutiveReports.riskHigh') : t('ExecutiveReports.riskCritical');

  const r = 48;
  const circumference = Math.PI * r;
  const dashOffset = circumference * (1 - pct / 100);

  return (
    <div style={{ textAlign: 'center' }}>
      <svg width="120" height="70" viewBox="0 0 120 70">
        <path d={`M 12 60 A 48 48 0 0 1 108 60`} fill="none" stroke="#e5e7eb" strokeWidth="10" strokeLinecap="round" />
        <path d={`M 12 60 A 48 48 0 0 1 108 60`} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
              strokeDasharray={circumference} strokeDashoffset={dashOffset} style={{ transition: 'stroke-dashoffset .8s' }} />
        <text x="60" y="56" textAnchor="middle" fontSize="22" fontWeight="700" fill="#111827">{pct}</text>
      </svg>
      <div style={{ fontSize: 12, fontWeight: 600, color }}>{label}</div>
    </div>
  );
}

function Sparkline({ data }) {
  const { t } = useTranslation();
  if (!data || data.length < 2) return <div style={{ color: '#9ca3af', fontSize: 12 }}>{t('ExecutiveReports.notEnoughData')}</div>;
  const scores = data.map(d => d.score || 0);
  const min = Math.min(...scores), max = Math.max(...scores);
  const range = max - min || 1;
  const W = 240, H = 50;
  const pts = scores.map((s, i) => {
    const x = (i / (scores.length - 1)) * W;
    const y = H - ((s - min) / range) * (H - 8) - 4;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={W} height={H} style={{ overflow: 'visible' }}>
      <polyline points={pts} fill="none" stroke="#6366f1" strokeWidth="2" strokeLinejoin="round" />
      {scores.map((s, i) => {
        const x = (i / (scores.length - 1)) * W;
        const y = H - ((s - min) / range) * (H - 8) - 4;
        return <circle key={i} cx={x} cy={y} r="3" fill="#6366f1" />;
      })}
    </svg>
  );
}

export default function ExecutiveReports() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [templates, setTemplates]   = useState([]);
  const [reports, setReports]       = useState([]);
  const [riskScore, setRiskScore]   = useState(null);
  const [trend, setTrend]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [creating, setCreating]     = useState(false);
  const [selected, setSelected]     = useState(null);
  const [narrative, setNarrative]   = useState('');
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState('');
  const [tab, setTab]               = useState('dashboard');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, rRes, rsRes, trRes] = await Promise.all([
        axiosInstance.get('/reports/templates',        { headers: authHeaders() }),
        axiosInstance.get('/reports',                  { headers: authHeaders() }),
        axiosInstance.get('/reports/risk-score',       { headers: authHeaders() }),
        axiosInstance.get('/reports/risk-score/trend', { headers: authHeaders() }),
      ]);
      setTemplates(tRes.data || []);
      setReports(rRes.data || []);
      setRiskScore(rsRes.data || null);
      setTrend(trRes.data || []);
    } catch {
      setError(t('ExecutiveReports.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const createDraft = async (templateId) => {
    setCreating(true);
    setMsg('');
    try {
      const res = await axiosInstance.post('/reports', { templateId }, { headers: authHeaders() });
      setReports(prev => [res.data, ...prev]);
      setSelected(res.data);
      setNarrative(res.data.narrativeJson || '');
      setTab('editor');
    } catch {
      setMsg(t('ExecutiveReports.errorCreateDraft'));
    } finally {
      setCreating(false);
    }
  };

  const saveNarrative = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await axiosInstance.put(`/reports/${selected.id}`, { narrativeJson: narrative }, { headers: authHeaders() });
      setSelected(res.data);
      setMsg(t('ExecutiveReports.savedMsg'));
    } catch {
      setMsg(t('ExecutiveReports.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const finalise = async () => {
    if (!selected) return;
    if (!window.confirm(t('ExecutiveReports.finaliseConfirm'))) return;
    try {
      const res = await axiosInstance.post(`/reports/${selected.id}/finalise`, {}, { headers: authHeaders() });
      setSelected(res.data);
      setReports(prev => prev.map(r => r.id === res.data.id ? res.data : r));
      setMsg(t('ExecutiveReports.reportFinalised'));
    } catch {
      setMsg(t('ExecutiveReports.finaliseFailed'));
    }
  };

  const openReport = (r) => {
    setSelected(r);
    setNarrative(r.narrativeJson || '');
    setTab('editor');
  };

  // Arriving with ?open=<report id> (the AML overview does this after it generates the committee
  // report) goes straight to that report instead of the dashboard tab.
  useEffect(() => {
    const openId = Number(searchParams.get('open'));
    if (!openId || selected) return;
    const report = reports.find((r) => r.id === openId);
    if (report) openReport(report);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  const s = {
    page:  { padding: 24, fontFamily: 'Inter, sans-serif', maxWidth: 1100 },
    h1:    { fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 4 },
    sub:   { fontSize: 13, color: '#6b7280', marginBottom: 20 },
    tabs:  { display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: 20 },
    tab:   (a) => ({ padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
                     fontWeight: 600, fontSize: 13, color: a ? '#6366f1' : '#6b7280',
                     borderBottom: a ? '2px solid #6366f1' : '2px solid transparent', marginBottom: -2 }),
    card:  { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginBottom: 16 },
    grid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 },
    msg:   { padding: '8px 14px', borderRadius: 6, fontSize: 13, marginBottom: 12,
              background: '#dbeafe', color: '#1e40af' },
    btn:   (c = '#6366f1') => ({
              padding: '7px 16px', background: c, color: '#fff', border: 'none',
              borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }),
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}><i className="fas fa-spinner fa-spin" /></div>;
  if (error)   return <div style={{ padding: 20, color: '#dc2626' }}>{error}</div>;

  const parsedNarrative = (() => {
    if (!selected?.narrativeJson) return null;
    try { return JSON.parse(selected.narrativeJson); } catch { return null; }
  })();

  return (
    <div style={s.page}>
      <div style={s.h1}>{t('ExecutiveReports.title')}</div>
      <div style={s.sub}>{t('ExecutiveReports.subtitle')}</div>

      {msg && <div style={s.msg}>{msg}</div>}

      <div style={s.tabs}>
        {['dashboard', 'templates', 'reports', ...(selected ? ['editor'] : [])].map(tabKey => (
          <button key={tabKey} style={s.tab(tab === tabKey)} onClick={() => setTab(tabKey)}>
            {tabKey === 'dashboard'  && t('ExecutiveReports.tabDashboard')}
            {tabKey === 'templates'  && t('ExecutiveReports.tabTemplates')}
            {tabKey === 'reports'    && t('ExecutiveReports.tabReports')}
            {tabKey === 'editor'     && (selected?.title || t('ExecutiveReports.tabEditor'))}
          </button>
        ))}
      </div>

      {/* ── Risk Dashboard ───────────────────────────────────────────── */}
      {tab === 'dashboard' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20 }}>
            {/* Gauge */}
            <div style={{ ...s.card, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#374151' }} title={t('ExecutiveReports.riskScoreTooltip')}>{t('ExecutiveReports.currentRiskScore')}</div>
              <RiskGauge score={riskScore?.score} />
              {riskScore && (
                <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
                  <div>{t('ExecutiveReports.complianceDeficit')} <b>{t('ExecutiveReports.points', { count: riskScore.compliance?.deduction || 0 })}</b></div>
                  <div>{t('ExecutiveReports.failedControls')}    <b>{t('ExecutiveReports.points', { count: riskScore.openFindings?.deduction || 0 })}</b></div>
                  <div style={{ marginTop: 4, fontSize: 11 }}>{t('ExecutiveReports.updatedOn', { date: riskScore.scoreDate || t('ExecutiveReports.today') })}</div>
                </div>
              )}
            </div>

            {/* Trend sparkline */}
            <div style={s.card}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#374151' }}>{t('ExecutiveReports.ninetyDayTrend')}</div>
              {trend.length > 1 ? (
                <>
                  <Sparkline data={trend} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
                    <span>{trend[0]?.scoreDate}</span>
                    <span>{trend[trend.length - 1]?.scoreDate}</span>
                  </div>
                  <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {[
                      { label: t('ExecutiveReports.statMin'), val: Math.min(...trend.map(d => d.score || 0)) },
                      { label: t('ExecutiveReports.statMax'), val: Math.max(...trend.map(d => d.score || 0)) },
                      { label: t('ExecutiveReports.statAvg'), val: Math.round(trend.reduce((a, d) => a + (d.score || 0), 0) / trend.length) },
                    ].map(stat => (
                      <div key={stat.label} style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 16px', textAlign: 'center' }}>
                        <div style={{ fontSize: 18, fontWeight: 700 }}>{stat.val}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: 20 }}>
                  {t('ExecutiveReports.trendEmpty')}
                </div>
              )}
            </div>
          </div>

          {/* Recommended actions from latest report */}
          {parsedNarrative?.recommendedActions?.length > 0 && (
            <div style={s.card}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t('ExecutiveReports.recommendedActions')}</div>
              {parsedNarrative.recommendedActions.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#6366f1', color: '#fff',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                    {i + 1}
                  </span>
                  <span style={{ fontSize: 13 }}>{a}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Templates ────────────────────────────────────────────────── */}
      {tab === 'templates' && (
        <div style={s.grid}>
          {templates.map(tpl => {
            const meta = TEMPLATE_META[tpl.id] || {};
            return (
              <div key={tpl.id} style={{ ...s.card, borderTop: `4px solid ${meta.color || '#6366f1'}` }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: meta.color || '#6366f1',
                                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={`fas ${meta.icon || 'fa-file'}`} style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{tpl.name}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{meta.pages ? t('ExecutiveReports.pageCount', { count: meta.pages }) : ''}</div>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>{meta.descKey ? t(`ExecutiveReports.${meta.descKey}`) : tpl.description}</div>
                <button
                  onClick={() => createDraft(tpl.id)}
                  disabled={creating}
                  style={s.btn(meta.color || '#6366f1')}>
                  {creating ? <i className="fas fa-spinner fa-spin" /> : t('ExecutiveReports.generateDraft')}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Reports list ─────────────────────────────────────────────── */}
      {tab === 'reports' && (
        <div style={s.card}>
          {reports.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: 40 }}>
              {t('ExecutiveReports.noReports')}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {[t('ExecutiveReports.colTitle'), t('ExecutiveReports.colTemplate'), t('ExecutiveReports.colStatus'), t('ExecutiveReports.colCreated'), ''].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.map(r => {
                  const sb = STATUS_BADGE[r.status] || STATUS_BADGE.DRAFT;
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>{r.title}</td>
                      <td style={{ padding: '10px 12px', color: '#6b7280' }}>{r.templateId}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, ...sb }}>{r.status}</span>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#6b7280' }}>{r.createdAt?.slice(0, 10)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <button onClick={() => openReport(r)}
                          style={{ ...s.btn(), padding: '4px 12px', fontSize: 12 }}>
                          {t('ExecutiveReports.open')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Editor ───────────────────────────────────────────────────── */}
      {tab === 'editor' && selected && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{selected.title}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{selected.templateId} · {selected.status}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {selected.status !== 'FINALISED' && (
                <>
                  <button onClick={saveNarrative} disabled={saving} style={s.btn()}>
                    {saving ? t('ExecutiveReports.saving') : t('ExecutiveReports.save')}
                  </button>
                  <button onClick={finalise} style={s.btn('#16a34a')}>
                    {t('ExecutiveReports.finalise')}
                  </button>
                </>
              )}
              {selected.pdfUrl && (
                <a href={selected.pdfUrl} target="_blank" rel="noreferrer"
                   style={{ ...s.btn('#374151'), textDecoration: 'none', display: 'inline-block' }}>
                  <i className="fas fa-download" /> PDF
                </a>
              )}
            </div>
          </div>

          {/* Generated narrative summary */}
          {parsedNarrative && (
            <div style={{ ...s.card, background: '#f9fafb', marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>{t('ExecutiveReports.autoGeneratedNarrative')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
                <div style={{ background: '#fff', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{parsedNarrative.riskScore ?? '—'}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{t('ExecutiveReports.riskScoreLabel')}</div>
                </div>
                <div style={{ background: '#fff', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: parsedNarrative.riskLevel === 'LOW' ? '#16a34a' : '#dc2626' }}>
                    {parsedNarrative.riskLevel}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{t('ExecutiveReports.riskLevelLabel')}</div>
                </div>
                <div style={{ background: '#fff', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{parsedNarrative.trend ?? '—'}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{t('ExecutiveReports.trendLabel')}</div>
                </div>
              </div>
              {parsedNarrative.recommendedActions?.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{t('ExecutiveReports.recommendedActions')}</div>
                  {parsedNarrative.recommendedActions.map((a, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#374151', padding: '3px 0' }}>• {a}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Editable narrative */}
          {selected.status !== 'FINALISED' && (
            <div style={s.card}>
              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>{t('ExecutiveReports.customNarrative')}</div>
              <textarea
                value={narrative}
                onChange={e => setNarrative(e.target.value)}
                rows={10}
                placeholder={t('ExecutiveReports.narrativePlaceholder')}
                style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: 10, fontSize: 13,
                          fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
          )}

          {selected.status === 'FINALISED' && (
            <div style={{ ...s.card, background: '#f0fdf4', border: '1px solid #86efac' }}>
              <i className="fas fa-lock" style={{ marginRight: 8, color: '#16a34a' }} />
              <span style={{ color: '#166534', fontSize: 13 }}>{t('ExecutiveReports.finalisedLocked')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
